import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  GET_QUOTE,
  GET_QUOTE_PDF,
  GET_QUOTE_REVISION_LIST,
  GET_ARCHIVED_QUOTE,
} from '../../../../queries';
// material
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Divider from '@mui/material/Divider';
import TableRow from '@mui/material/TableRow';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// utils
import { fCurrency } from '../../utils/formatNumber';
import {
  downloadPdfFile,
  extractLocationDetailsFromAnswerInstance,
} from '../../../common/QuoteLoader/utility';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import {
  QuoteToolbar,
  QuoteHasNextEditionWarning,
  QuoteAxisStructuredDataDisplay,
} from '../../components/_dashboard/quotes/details';
import LoadingScreen from '../../components/LoadingScreen';
import {
  QuoteCollapsibleRow,
  QuoteLocationRow,
} from '../../components/_dashboard/quotes/details';
import { SelectChangeEvent } from '@mui/material/Select';
import { InsurerQuoteComparison } from './InsurerQuoteDetails/InsurerQuoteComparison';
// hooks
import { useAuth } from '@calefy-inc/authentication';
import {
  useSettings as useCompanySettings,
  useAllowByPermission,
  useAllowByAdminPermissions,
  useUsingVagoSettings,
  useSupportEmail,
} from '../../../../hooks';
import { useSnackbar } from 'notistack';
import { sortForms } from '../../../common/QuotePdf/components/utility';
import { styled } from '@mui/material';
import { sortByInternalName as sortByPolicyStreamInternalName } from '../../../PolicyStream/PolicyStreamReviewPage/utility';
import { QuoteEditionsComparison } from '../../components/_dashboard/quotes/details/QuoteEditionsComparison';

import { ReviewPageDisplaySelectedPlan } from '../../../PolicyStream/PolicyStreamReviewPage/PolicyStreamReviewPage';

// error handlers
import {
  InvalidUUIDError,
  MissingQuoteError,
} from '../../../QuoteWizard/QuoteResume/ErrorComponents';
import {
  handleErrors,
  InsufficientPermissionsErrorPage,
} from '../../../common/ErrorHandling';
import Bugsnag from '@bugsnag/js';
import { PremiumOverrides } from './PremiumOverrides';
import { QuoteEditionsDisplay } from '../../components/_dashboard/quotes/details/QuoteEditionsDisplay';

// classes and types
import { Quote } from '../../../../Typescript/backend/classes';
import type { HandleableError } from '../../../../components/common/ErrorHandling';
import type { QuoteReviewLocationDetails } from './types';
import { generateColourFromQuoteStatus } from './utility';
import { RouteComponentProps } from '@reach/router';
import { QuoteBoundStatus } from './QuoteBoundStatus';
import type { PolicystreamPlan } from '../../../../store/policystreamStore';

// ----------------------------------------------------------------------
// types

type RevisionListType = {
  quoteId: string;
  dateAdded: string;
};

enum TAB_VALUES {
  QUOTE_DETAILS = 'QUOTE DETAILS',
  PREMIUM_OVERRIDES = 'PREMIUM OVERRIDES',
  AI = 'AI',
  VERSIONS_COMPARISON = 'VERSIONS_COMPARISON',
}

// ----------------------------------------------------------------------
// utility

const quoteHasStructuredDataToDisplay = (quote: Quote): boolean => {
  const { structuredData } = quote;

  return Boolean(
    structuredData?.contactName ||
      structuredData?.businessName ||
      structuredData?.contactAddress?.formattedAddress ||
      structuredData?.contactEmail ||
      structuredData?.contactPhoneNumber ||
      quote.producerId ||
      (structuredData?.revenue !== undefined &&
        structuredData?.revenue !== null) ||
      quote.businessLine?.displayName ||
      structuredData?.alreadyContactedBrokerName ||
      quote.referralCode,
  );
};

/**
 * Component-specific error handling
 * @param error - the error to process
 * @return - Either return a valid react element or nothing (to have control pass back to the handler function)
 */
const baseHandleSpecificErrors = (error: HandleableError, uuid: string) => {
  if (/invalid permissions/i.test(error.message)) {
    // this will probably never fire, but is here for safety
    return (
      <InsufficientPermissionsErrorPage deniedIdentifier='Quote Details page' />
    );
  }
  if (error.message.match(/is not a valid uuid/i)) {
    return <InvalidUUIDError uuid={uuid} />;
  }
  if (error.message.match(/quote matching query does not exist/i)) {
    return <MissingQuoteError uuid={uuid} />;
  }
};

// ----------------------------------------------------------------------
// component

/**
 * Dispays the details of a quote and provides the ability to edit a quote
 * or download a copy of the quotes pdf
 *
 * @param {string} uniqueID: the unique id of the quote we want to display
 */
interface QuoteDetailsProps extends RouteComponentProps {
  uniqueID?: string;
}
export default function QuoteDetails({ uniqueID }: QuoteDetailsProps) {
  const {
    settings: { logo },
  } = useCompanySettings();
  const { token } = useAuth();
  const hasViewQuoteDetailsPermission =
    useAllowByPermission('view:quote:details');
  const { enqueueSnackbar } = useSnackbar();
  const supportEmail = useSupportEmail();

  // ----------------------------------------------------------------------
  // state

  const [quote, setQuote] = useState<Quote | null>(null);
  const [revisionList, setRevisionList] = useState<RevisionListType | []>([]);
  const [selectedRevision, setSelectedRevision] = useState<string>('0');
  const [isArchived, setIsArchived] = useState<boolean>(false);
  const [previousArchivedID, setPreviousArchivedID] = useState<
    string | null | undefined
  >(null);
  const [status, setStatus] = useState<Quote['status'] | null>(null); // clean up the quote status for display
  const [locationData, setLocationData] = useState<
    Record<
      QuoteReviewLocationDetails['general']['id'],
      QuoteReviewLocationDetails
    >
  >({});
  const [renewalOnly, setRenewalOnly] = useState<boolean>(false);
  const displayAiQuoteComparison = useDisplayQuoteComparison(quote);
  const displayPremiumOverrides = useDisplayPremiumOverrides(quote);
  const displayOtherVersionsTab = useDisplayOtherVersionsTab();
  const [tabsToDisplay, setTabsToDisplay] = useState<Array<TAB_VALUES>>([
    TAB_VALUES.QUOTE_DETAILS,
  ]);
  const [tabValue, setTabValue] = useState<number>(0);
  const [policystreamPlan, setPolicyStreamPlan] =
    useState<PolicystreamPlan | null>(null);
  const [policyStreamPlanFrequency, setPolicyStreamPlanFrequency] = useState<
    string | null
  >(null);

  // ----------------------------------------------------------------------
  // queries

  // load current quote
  const [loadQuote, { called, loading, error, data }] = useLazyQuery(
    GET_QUOTE,
    {
      fetchPolicy: 'no-cache',
    },
  );

  // load revision list
  const [
    loadQuoteRevisionList,
    { data: revisionListData, error: revisionListError },
  ] = useLazyQuery(GET_QUOTE_REVISION_LIST);

  // load archived quote
  const [
    loadArchivedQuote,
    {
      data: archivedQuoteData,
      loading: archivedQuoteLoading,
      error: archivedQuoteError,
    },
  ] = useLazyQuery(GET_ARCHIVED_QUOTE);

  // load pdf query
  const [
    loadQuotePdf,
    { data: pdfData, loading: pdfLoading, error: pdfError },
  ] = useLazyQuery(GET_QUOTE_PDF, {
    fetchPolicy: 'no-cache',
  });

  // ----------------------------------------------------------------------
  // state managers

  // update the tabs to display as we get the information
  useEffect(() => {
    const tabs: Array<TAB_VALUES> = [TAB_VALUES.QUOTE_DETAILS];

    if (displayPremiumOverrides) {
      tabs.push(TAB_VALUES.PREMIUM_OVERRIDES);
    }
    if (displayAiQuoteComparison) {
      tabs.push(TAB_VALUES.AI);
    }
    if (displayOtherVersionsTab) {
      tabs.push(TAB_VALUES.VERSIONS_COMPARISON);
    }

    setTabsToDisplay(tabs);
  }, [
    displayPremiumOverrides,
    displayAiQuoteComparison,
    displayOtherVersionsTab,
  ]);

  // when the page is loaded, fetch the quote
  useEffect(() => {
    if (token && hasViewQuoteDetailsPermission && uniqueID) {
      loadQuote({ variables: { uID: uniqueID, token } });
    }
  }, [uniqueID, token, loadQuote, hasViewQuoteDetailsPermission]);

  // when quote is successfully loaded, add it to state and get the revision list
  useEffect(() => {
    if (data && data.quote) {
      setQuote(Quote.generateFromBackendResponse(data.quote));
      //@ts-expect-error
      loadQuoteRevisionList({ variables: { token, uniqueID }, skip: !token });

      if (data.quote?.policystreamPlan) {
        setPolicyStreamPlan(data.quote.policystreamPlan);
      }
      if (data.quote?.policystreamPlanPaymentFrequency) {
        setPolicyStreamPlanFrequency(
          data.quote.policystreamPlanPaymentFrequency,
        );
      }
    }
  }, [data, token, loadQuoteRevisionList, uniqueID]);

  // when revision list is loaded, add it to state if any exist
  useEffect(() => {
    // @ts-expect-error
    if (revisionListData?.quoteRevisions?.length > 0) {
      // @ts-expect-error
      setRevisionList(revisionListData?.quoteRevisions);
    }
  }, [revisionListData]);

  // when the selected revision changes, fetch data for the selected revision
  useEffect(() => {
    if (selectedRevision !== '0') {
      if (selectedRevision === previousArchivedID) {
        setLocationData({});
        setQuote(
          Quote.generateFromBackendResponse(archivedQuoteData?.archivedQuote),
        );
        setIsArchived(true);
      } else {
        setPreviousArchivedID(null);
        loadArchivedQuote({
          variables: { token, archiveID: selectedRevision },
        });
      }
    } else {
      setPreviousArchivedID(quote?.id);
      if (data?.quote) {
        setLocationData({});
        setQuote(Quote.generateFromBackendResponse(data.quote));
        setIsArchived(false);
      }
    }
  }, [
    selectedRevision,
    archivedQuoteData?.archivedQuote,
    data?.quote,
    loadArchivedQuote,
    previousArchivedID,
    quote?.id,
    token,
  ]);

  // when archive quote is loaded, set it as the quote to display
  useEffect(() => {
    if (archivedQuoteData?.archivedQuote) {
      setLocationData({});
      setQuote(
        Quote.generateFromBackendResponse(archivedQuoteData?.archivedQuote),
      );
      setIsArchived(true);
    }
  }, [archivedQuoteData]);

  // when archived quote query has any error notify bugsnag
  useEffect(() => {
    if (archivedQuoteError) {
      const msg = 'Error getting archived revision';
      enqueueSnackbar(msg, { variant: 'error' });
      Bugsnag.notify(`${msg}: ${archivedQuoteError}`);
    }
  }, [archivedQuoteError, enqueueSnackbar]);

  // when revision list has an error notify bugsnag
  useEffect(() => {
    if (revisionListError) {
      const msg = 'Error getting revision list';
      enqueueSnackbar(msg, { variant: 'error' });
      Bugsnag.notify(`${msg}: ${revisionListError}`);
    }
  }, [revisionListError, enqueueSnackbar]);

  // when pdf is loaded, download pdf and display completion message
  useEffect(() => {
    if (pdfData && pdfData.quotePdf?.pdfString) {
      const quotePdf = pdfData.quotePdf;
      downloadPdfFile(
        // @ts-expect-error
        quotePdf.pdfString,
        `${quotePdf.businessName || 'New Application'}.pdf`,
      );
      enqueueSnackbar('Download Complete', { variant: 'success' });
    }
  }, [pdfData, enqueueSnackbar]);

  // when there is an error getting the pdf, display error message and notify bugsnag
  useEffect(() => {
    if (pdfError) {
      enqueueSnackbar('Download Failed', { variant: 'error' });
      Bugsnag.notify(`Pdf download failed: ${pdfError}`);
    } else if (
      pdfData &&
      (pdfData?.quotePdf === null || pdfData?.quotePdf?.pdfString === null)
    ) {
      enqueueSnackbar('Download Failed - Unable to Create PDF', {
        variant: 'error',
      });
      Bugsnag.notify(`Pdf download failed: ${pdfData}`);
    }
  }, [pdfError, pdfData, enqueueSnackbar]);

  // when the quote is loaded, extract the relevant location answers
  useEffect(() => {
    if (!quote) {
      return;
    }
    const allLocations = quote.allUniqueAnsweredLocationAnswers();
    const locationsWithBasicDetails = allLocations.map(
      extractLocationDetailsFromAnswerInstance,
    );
    setLocationData((oldLocationData) => {
      return Object.assign(
        {},
        oldLocationData,
        locationsWithBasicDetails.reduce(
          (
            acc: Record<
              QuoteReviewLocationDetails['general']['id'],
              QuoteReviewLocationDetails
            >,
            location,
          ) => {
            acc[location.general.id] = location;
            return acc;
          },
          {},
        ),
      );
    });
    setStatus(quote.status);
  }, [quote]);

  // ----------------------------------------------------------------------
  // status actions

  if (!uniqueID) {
    return null;
  }

  // ----------------------------------------------------------------------
  // handlers

  const handlePdfDownload = (downloadType?: string) => {
    if (token) {
      loadQuotePdf({
        variables: {
          token,
          uniqueID,
          archiveID: selectedRevision !== '0' ? selectedRevision : null,
          renewalOnly: renewalOnly,
          downloadType,
        },
      });
    }
  };

  const handleRevisionChange = (event: SelectChangeEvent) => {
    setSelectedRevision(event?.target.value);
  };

  const handleRenewalOnlyChange = () => {
    setRenewalOnly(!renewalOnly);
  };

  const handleTabValueChange = (
    _event: React.SyntheticEvent,
    newTabValue: number,
  ) => {
    setTabValue(newTabValue);
  };

  return (
    <Page title='Quote: View Details | Manager'>
      <Container maxWidth={'lg'} data-testid='quote-details'>
        {hasViewQuoteDetailsPermission === false ? (
          <InsufficientPermissionsErrorPage deniedIdentifier='Quote Details page' />
        ) : loading || !called || archivedQuoteLoading ? (
          <LoadingScreen />
        ) : error ? (
          handleErrors(
            error,
            (
              (uuid: string) => (error: HandleableError) =>
                baseHandleSpecificErrors(error, uuid)
            )(uniqueID),
          )
        ) : !uniqueID || !quote ? null : (
          <>
            <HeaderBreadcrumbs
              heading='Quote Details'
              links={[
                { name: 'Dashboard', href: PATH_DASHBOARD.general.dashboard },
                { name: 'Quotes', href: PATH_DASHBOARD.quotes.root },
                { name: 'Quote' },
              ]}
            />
            <QuoteToolbar
              quote={quote}
              setStatus={setStatus}
              onDownload={handlePdfDownload}
              pdfStatus={pdfLoading}
              selectedRevision={selectedRevision}
              revisionList={revisionList}
              handleRevisionChange={handleRevisionChange}
              isArchived={isArchived}
              renewalOnly={renewalOnly}
              handleRenewalOnlyChange={handleRenewalOnlyChange}
            />
            <QuoteHasNextEditionWarning quote={quote} />
            <Card sx={{ pt: 5, px: 5 }}>
              <Tabs
                onChange={handleTabValueChange}
                value={tabValue}
                sx={{ mb: 3 }}
              >
                <Tab label='Application Details' />
                {displayPremiumOverrides ? (
                  <Tab label='Premium Overrides' />
                ) : null}
                {displayAiQuoteComparison ? (
                  <Tab label='Insurer Quotes' />
                ) : null}
                {displayOtherVersionsTab ? (
                  <Tab label='Compare Editions' />
                ) : null}
              </Tabs>
              <TabPanel
                tabValue={tabValue}
                index={tabsToDisplay.indexOf(TAB_VALUES.QUOTE_DETAILS)}
              >
                <QuoteEditionsDisplayContainer>
                  <QuoteEditionsDisplay quote={quote} />
                </QuoteEditionsDisplayContainer>
                <Grid container>
                  <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
                    <Box
                      component='img'
                      alt='logo'
                      src={logo}
                      sx={{ height: 48 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
                    <Box sx={{ textAlign: { sm: 'right' } }}>
                      <Label
                        color={generateColourFromQuoteStatus(status)}
                        sx={{ textTransform: 'uppercase', mb: 1 }}
                        data-testId='quoteStatusChip'
                      >
                        {status && status.replace(/_/g, ' ')}
                      </Label>
                      <Typography variant='h6'>
                        Quote - {quote.getDisplayId()}
                      </Typography>
                    </Box>
                  </Grid>

                  <QuoteMiscellanyContainer>
                    <QuoteStructuredDataColumn>
                      {quoteHasStructuredDataToDisplay(quote) ? (
                        <QuoteStructuredDataContainer>
                          <Typography
                            paragraph
                            variant='overline'
                            sx={{ color: 'text.disabled' }}
                          >
                            Quote from
                          </Typography>
                          {[
                            quote.structuredData?.contactName,
                            quote.structuredData?.businessName,
                            quote.structuredData?.contactAddress
                              ?.formattedAddress,
                            quote.structuredData?.contactEmail,
                            quote.structuredData?.contactPhoneNumber,
                          ]
                            .filter((s) => !!s)
                            .map((info, index) => (
                              <Typography
                                key={`${index}-${String(info)}`}
                                variant='body2'
                              >
                                {info}
                              </Typography>
                            ))}
                          {quote.producerId ? (
                            <Typography variant='body2'>
                              Producer ID: {quote.producerId}
                            </Typography>
                          ) : null}

                          {quote.structuredData?.revenue === undefined ||
                          quote.structuredData?.revenue === null ? null : (
                            <Typography variant='body2'>
                              Revenue: {fCurrency(quote.structuredData.revenue)}
                            </Typography>
                          )}
                          {quote.businessLine?.displayName ? (
                            <Typography variant='body2'>
                              Line: {quote.businessLine.displayName}
                            </Typography>
                          ) : null}
                          {quote.structuredData?.alreadyContactedBrokerName ? (
                            <Typography variant='body2'>
                              <span style={{ fontWeight: 'bold' }}>
                                Broker:{' '}
                              </span>
                              {quote.structuredData?.alreadyContactedBrokerName}
                            </Typography>
                          ) : null}
                          {quote?.referralCode ? (
                            <Typography variant='body2'>
                              <span style={{ fontWeight: 'bold' }}>
                                Referral Code:{' '}
                              </span>
                              <Tooltip
                                title={
                                  ('description' in quote.referralCode &&
                                    // @ts-expect-error
                                    quote.referralCode.description) ||
                                  quote.referralCode.code
                                }
                              >
                                <span style={{ fontWeight: 'bold' }}>
                                  {quote.referralCode?.code}
                                </span>
                              </Tooltip>
                            </Typography>
                          ) : null}
                        </QuoteStructuredDataContainer>
                      ) : null}
                      <QuoteAxisStructuredDataDisplay
                        axisStructuredData={quote.axisStructuredData}
                      />
                    </QuoteStructuredDataColumn>
                    <QuoteBoundStatusContainer>
                      <QuoteBoundStatus quote={quote} />
                    </QuoteBoundStatusContainer>
                  </QuoteMiscellanyContainer>
                </Grid>

                <Scrollbar>
                  <TableContainer>
                    {quote.isPolicyStreamApplication && policystreamPlan ? (
                      <>
                        <h3>Selected Plan:</h3>
                        <ReviewPageDisplaySelectedPlan
                          plan={policystreamPlan}
                          planFrequency={policyStreamPlanFrequency || undefined}
                          displayButton={false}
                        />
                      </>
                    ) : null}
                    <Table>
                      <TableHead
                        sx={{
                          borderBottom: (theme) =>
                            `solid 1px ${theme.palette.divider}`,
                          '& th': { backgroundColor: 'transparent' },
                        }}
                      >
                        <TableRow>
                          <TableCell align='left'>Forms</TableCell>
                          <TableCell width={40} />
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {(quote.isPolicyStreamApplication
                          ? [...quote.completedForms].sort(
                              sortByPolicyStreamInternalName,
                            )
                          : sortForms(quote.completedForms)
                        ).map((form, index) => {
                          return (
                            <QuoteCollapsibleRow
                              key={form.id}
                              form={form}
                              index={index}
                              quoteStatus={quote.status}
                              renewalOnly={renewalOnly}
                            />
                          );
                        })}
                      </TableBody>
                    </Table>

                    {Object.keys(locationData).length > 0 && (
                      <Table>
                        <TableHead
                          sx={{
                            borderBottom: (theme) =>
                              `solid 1px ${theme.palette.divider}`,
                            '& th': { backgroundColor: 'transparent' },
                          }}
                        >
                          <TableRow>
                            <TableCell align='left'>Locations</TableCell>
                            <TableCell width={40} />
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {Object.values(locationData).map(
                            (location, index) => (
                              <QuoteLocationRow
                                location={location}
                                index={index}
                                setLocationData={setLocationData}
                                loadQuotePdf={handlePdfDownload}
                                isArchived={isArchived}
                              />
                            ),
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </TableContainer>
                </Scrollbar>

                {quote.additionalInformation ? (
                  <>
                    <Box sx={{ my: 3 }}>
                      <Typography variant='subtitle2' sx={{ mb: 1 }}>
                        Additional information or comments
                      </Typography>
                      <Typography variant='body2'>
                        {quote.additionalInformation}
                      </Typography>
                    </Box>
                    <Divider />
                  </>
                ) : null}

                <Grid container>
                  <Grid item xs={12} md={9} sx={{ py: 3 }}></Grid>
                  <Grid item xs={12} md={3} sx={{ py: 3, textAlign: 'right' }}>
                    <Typography variant='subtitle2'>
                      Have a Question?
                    </Typography>
                    <a href={`mailto:${supportEmail}`}>
                      <Typography variant='body2'>{supportEmail}</Typography>
                    </a>
                  </Grid>
                </Grid>
              </TabPanel>
              {displayPremiumOverrides ? (
                <TabPanel
                  tabValue={tabValue}
                  index={tabsToDisplay.indexOf(TAB_VALUES.PREMIUM_OVERRIDES)}
                >
                  <PremiumOverrides quote={quote} />
                </TabPanel>
              ) : null}
              {displayAiQuoteComparison ? (
                <TabPanel
                  tabValue={tabValue}
                  index={tabsToDisplay.indexOf(TAB_VALUES.AI)}
                >
                  <InsurerQuoteComparison uniqueID={uniqueID} />
                </TabPanel>
              ) : null}
              {displayOtherVersionsTab ? (
                <TabPanel
                  tabValue={tabValue}
                  index={tabsToDisplay.indexOf(TAB_VALUES.VERSIONS_COMPARISON)}
                >
                  <QuoteEditionsComparison quote={quote} />
                </TabPanel>
              ) : null}
            </Card>
          </>
        )}
      </Container>
    </Page>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  tabValue: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, tabValue, index, ...other } = props;

  return (
    <div role='tabpanel' hidden={tabValue !== index} id={`${index}`} {...other}>
      {children}
    </div>
  );
}

/**
 * Determines whether we should display the premium overrides. This is probably overkill for now (since it's only VaGo, but it should be relatively easy to update. If we get a bunch of these we should probably just move it to be part of website settings)
 */
const useDisplayPremiumOverrides = (quote?: Quote | null) => {
  const onVago = useUsingVagoSettings();

  if (
    (!quote ? true : !quote.isPolicyStreamApplication) &&
    [onVago].some((onPremiumOverrideOrg) => !!onPremiumOverrideOrg)
  ) {
    return true;
  }
  return false;
};

/**
 * Determines whether we should display the AI quote comparison. This is probably overkill for now. If we get a bunch of these we should probably just move it to be part of website settings)
 */
export const useDisplayQuoteComparison = (quote?: Quote | null) => {
  const hasAdminPermissions = useAllowByAdminPermissions();
  return (
    (quote ? !quote.isPolicyStreamApplication : true) && hasAdminPermissions
  );
};

/**
 * Determine whether or not to display the comparisons. ATM this is limited to vago, but at some point we may want to tune this up.
 */
export const useDisplayOtherVersionsTab = () => {
  const onVago = useUsingVagoSettings();
  return onVago;
};

const QuoteEditionsDisplayContainer = styled('div')(() => {
  return {
    display: 'flex',
    justifyContent: 'center',
  };
});
const QuoteMiscellanyContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
});
const QuoteStructuredDataColumn = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});
const QuoteStructuredDataContainer = styled('div')();
const QuoteBoundStatusContainer = styled('div')({
  flexGrow: 1,
});
